import { useEffect, useRef, useState } from 'react'

/**
 * Cloudflare Turnstile widget.
 *
 * The script is loaded once, on demand — only when the server says Turnstile
 * is configured — so an install without it pulls nothing from Cloudflare.
 * Tokens are single-use and expire after a few minutes, hence `reset()`
 * being exposed for the caller to invoke after a failed submit.
 */
declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: Record<string, unknown>) => string
      reset: (widgetId?: string) => void
      remove: (widgetId?: string) => void
    }
    onTurnstileReady?: () => void
  }
}

const SCRIPT_ID = 'cf-turnstile-script'
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve()

  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('load failed')))
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Could not load the human check.'))
    document.head.appendChild(script)
  })
}

export interface TurnstileHandle {
  reset: () => void
}

export function Turnstile({
  siteKey,
  onToken,
  onError,
  handleRef,
  theme = 'auto',
}: {
  siteKey: string
  onToken: (token: string | null) => void
  onError?: (message: string) => void
  handleRef?: (handle: TurnstileHandle) => void
  theme?: 'auto' | 'light' | 'dark'
}) {
  const container = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    loadScript()
      .then(() => {
        if (cancelled || !container.current || !window.turnstile) return
        // StrictMode mounts twice in development; never render two widgets.
        if (widgetId.current !== null) return

        widgetId.current = window.turnstile.render(container.current, {
          sitekey: siteKey,
          theme,
          callback: (token: string) => onToken(token),
          'expired-callback': () => onToken(null),
          'timeout-callback': () => onToken(null),
          'error-callback': () => {
            onToken(null)
            onError?.('The human check failed to load. Check your connection and try again.')
          },
        })

        handleRef?.({
          reset: () => {
            onToken(null)
            if (widgetId.current !== null) window.turnstile?.reset(widgetId.current)
          },
        })
      })
      .catch((error: Error) => {
        if (cancelled) return
        setFailed(error.message)
        onError?.(error.message)
      })

    return () => {
      cancelled = true
      if (widgetId.current !== null) {
        window.turnstile?.remove(widgetId.current)
        widgetId.current = null
      }
    }
    // Re-rendering the widget on every parent render would invalidate tokens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, theme])

  if (failed) {
    return (
      <p className="rounded-md border border-status-pending/40 bg-status-pending/10 px-3 py-2 text-xs text-status-pending">
        {failed}
      </p>
    )
  }

  return <div ref={container} className="flex min-h-[65px] justify-center" />
}
